import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../i18nContext';
import StaticPageLayout from './StaticPageLayout';
import { ArrowUpDown, Sparkles, Wrench, Zap, Shield, Bug } from 'lucide-react';

type SortOrder = 'newest' | 'oldest';

interface ChangelogEntry {
  version: string;
  date: string;
  type: 'feature' | 'improvement' | 'fix' | 'security';
  title: { zh: string; en: string };
  description: { zh: string; en: string };
}

const changelogData: ChangelogEntry[] = [
  {
    version: 'v2.4.0',
    date: '2026-02-21',
    type: 'feature',
    title: { zh: '咨询级逻辑提取引擎 v2.4', en: 'Consulting-Grade Synthesis v2.4' },
    description: {
      zh: '最新推理引擎升级显著提高了技术术语的准确性和多发言人重叠检测能力。支持更复杂的会议场景分析。',
      en: 'Our latest reasoning engine upgrade significantly improves technical terminology accuracy and multi-speaker overlapping detection. Supports more complex meeting scenario analysis.'
    }
  },
  {
    version: 'v2.3.2',
    date: '2026-02-15',
    type: 'improvement',
    title: { zh: '模板系统增强', en: 'Template System Enhancement' },
    description: {
      zh: '新增模板持久化绑定功能，每个任务可独立绑定编号模板。优化模板中心交互体验。',
      en: 'Added template persistence binding. Each task can independently bind a numbering template. Improved template center UX.'
    }
  },
  {
    version: 'v2.3.1',
    date: '2026-02-10',
    type: 'fix',
    title: { zh: '说话人及标题逻辑修复', en: 'Speaker & Title Logic Fix' },
    description: {
      zh: '修复了说话人识别和任务标题显示的问题。任务开始时显示文件名，获取到标题后自动切换显示。',
      en: 'Fixed speaker recognition and task title display issues. Shows filename at task start, auto-switches to title once available.'
    }
  },
  {
    version: 'v2.3.0',
    date: '2026-02-05',
    type: 'feature',
    title: { zh: '双币种定价与国际支付', en: 'Dual Currency Pricing & International Payments' },
    description: {
      zh: '支持根据 IP 地区自动切换 CNY/USD 定价。新增支付宝当面付和微信 Native 扫码支付。',
      en: 'Auto-switches CNY/USD pricing based on IP region. Added Alipay face-to-face and WeChat Native QR code payments.'
    }
  },
  {
    version: 'v2.2.0',
    date: '2026-01-20',
    type: 'feature',
    title: { zh: '增量文档整合', en: 'Incremental Document Integration' },
    description: {
      zh: '新增增量文档整合模块，支持 Agent→Validator→Applier 流程，实现 level1_index 两级定位精准整合。',
      en: 'New incremental document integration module with Agent→Validator→Applier pipeline and level1_index two-level positioning.'
    }
  },
  {
    version: 'v2.1.2',
    date: '2026-01-15',
    type: 'security',
    title: { zh: '注册风控流程升级', en: 'Registration Risk Control Upgrade' },
    description: {
      zh: '升级注册风控流程，增强设备指纹检测和异地登录识别能力。优化 Token 刷新机制。',
      en: 'Upgraded registration risk control with enhanced device fingerprinting and remote login detection. Optimized token refresh mechanism.'
    }
  },
  {
    version: 'v2.1.0',
    date: '2026-01-08',
    type: 'feature',
    title: { zh: '段落匹配双层架构', en: 'Two-Layer Paragraph Matching' },
    description: {
      zh: '段落匹配升级为双层架构：Layer1 LLM 匹配 level>=2，Layer2 模糊匹配阈值可配置，支持多对多落库。',
      en: 'Paragraph matching upgraded to two-layer architecture: Layer1 LLM matching for level>=2, Layer2 fuzzy matching with configurable threshold, supporting many-to-many storage.'
    }
  },
  {
    version: 'v2.0.0',
    date: '2025-12-20',
    type: 'feature',
    title: { zh: 'Word 导出与块编辑器', en: 'Word Export & Block Editor' },
    description: {
      zh: '全新块编辑器支持拖拽排序、框选、undo/redo。Word 导出完美还原 A4 排版，三栏布局精确到像素。',
      en: 'New block editor with drag-and-drop sorting, box selection, undo/redo. Word export perfectly reproduces A4 layout with pixel-precise three-column layout.'
    }
  },
  {
    version: 'v1.9.0',
    date: '2025-12-01',
    type: 'improvement',
    title: { zh: '音频格式自动转换', en: 'Auto Audio Format Conversion' },
    description: {
      zh: '前端集成 FFmpeg WASM，支持 WAV/MP4 自动转换为 MP3 后上传，减少用户操作步骤。',
      en: 'Integrated FFmpeg WASM on frontend for automatic WAV/MP4 to MP3 conversion before upload, reducing user steps.'
    }
  },
  {
    version: 'v1.8.0',
    date: '2025-11-15',
    type: 'feature',
    title: { zh: '会员体系与时长包', en: 'Membership System & Duration Packages' },
    description: {
      zh: '上线三级会员体系（Free/Pro/Team）和时长包购买功能。支持三池配额模型和 Co-founder 折扣。',
      en: 'Launched three-tier membership (Free/Pro/Team) and duration package purchases. Supports three-pool quota model and Co-founder discounts.'
    }
  },
];

const typeConfig = {
  feature: { icon: Sparkles, color: 'bg-emerald-100 text-emerald-700', label: { zh: '新功能', en: 'Feature' } },
  improvement: { icon: Zap, color: 'bg-blue-100 text-blue-700', label: { zh: '优化', en: 'Improvement' } },
  fix: { icon: Bug, color: 'bg-amber-100 text-amber-700', label: { zh: '修复', en: 'Fix' } },
  security: { icon: Shield, color: 'bg-purple-100 text-purple-700', label: { zh: '安全', en: 'Security' } },
};

interface Props { onBack: () => void; }

export default function ChangelogPage({ onBack }: Props) {
  const { language } = useLanguage();
  const en = language === 'en';
  const [sortOrder, setSortOrder] = useState<SortOrder>('newest');

  const sorted = [...changelogData].sort((a, b) => {
    const diff = new Date(b.date).getTime() - new Date(a.date).getTime();
    return sortOrder === 'newest' ? diff : -diff;
  });

  return (
    <StaticPageLayout title={en ? 'Changelog' : '更新日志'} onBack={onBack}>
      <Helmet>
        <title>{en ? 'Changelog - MinutesAI' : '更新日志 - MinutesAI'}</title>
        <meta name="description" content={en ? 'MinutesAI changelog: track every product update, new feature, improvement, and fix across all versions.' : 'MinutesAI 更新日志：追踪所有版本的产品更新、新功能、优化和修复记录。'} />
        <link rel="canonical" href="https://minutesai.com/changelog" />
        <meta property="og:title" content={en ? 'Changelog - MinutesAI' : '更新日志 - MinutesAI'} />
        <meta property="og:url" content="https://minutesai.com/changelog" />
      </Helmet>
      {/* Header */}
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold text-brand-900 font-serif mb-3">
          {en ? 'Changelog' : '更新日志'}
        </h2>
        <p className="text-lg text-ink-secondary max-w-2xl mx-auto">
          {en ? 'Track every improvement we make to Minutes AI.' : '追踪 Minutes AI 的每一次迭代与进化。'}
        </p>
      </div>

      {/* Sort Control */}
      <div className="flex justify-end mb-6">
        <button
          onClick={() => setSortOrder(s => s === 'newest' ? 'oldest' : 'newest')}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-surface-border text-sm text-brand-700 hover:text-brand-900 hover:border-surface-divider transition-colors"
        >
          <ArrowUpDown size={16} />
          {sortOrder === 'newest'
            ? (en ? 'Newest First' : '最新优先')
            : (en ? 'Oldest First' : '最早优先')}
        </button>
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-[19px] top-2 bottom-2 w-px bg-surface-border" />

        <div className="space-y-8">
          {sorted.map((entry) => {
            const cfg = typeConfig[entry.type];
            const Icon = cfg.icon;
            return (
              <div key={entry.version} className="relative pl-12">
                {/* Dot on timeline */}
                <div className="absolute left-[14px] top-[22px] w-[11px] h-[11px] rounded-full bg-brand-700 border-2 border-white shadow-sm" />

                <div className="bg-white rounded-2xl border border-surface-border p-6 hover:shadow-md transition-shadow">
                  {/* Top row: version + date + badge */}
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <span className="text-sm font-mono font-bold text-brand-900">{entry.version}</span>
                    <span className="text-xs text-ink-secondary">{entry.date}</span>
                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
                      <Icon size={12} />
                      {cfg.label[language]}
                    </span>
                  </div>

                  <h3 className="text-lg font-bold text-brand-900 mb-2">
                    {entry.title[language]}
                  </h3>
                  <p className="text-sm text-ink-secondary leading-relaxed">
                    {entry.description[language]}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </StaticPageLayout>
  );
}
